import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  ELEMENT_TRANSFORMERS,
  ElementTransformer,
  MULTILINE_ELEMENT_TRANSFORMERS,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  TextMatchTransformer,
  Transformer,
} from "@lexical/markdown";
import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  HorizontalRuleNode,
} from "@lexical/react/LexicalHorizontalRuleNode";
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import {
  $createTextNode,
  $isParagraphNode,
  $isTextNode,
  LexicalNode,
} from "lexical";

import { $createImageNode, $isImageNode, ImageNode } from "./nodes/ImageNode";
import emojiList from "./emoji-list";

export const HR: ElementTransformer = {
  dependencies: [HorizontalRuleNode],
  export: (node: LexicalNode) => {
    return $isHorizontalRuleNode(node) ? "***" : null;
  },
  regExp: /^(---|\*\*\*|___)\s?$/,
  replace: (parentNode, _1, _2, isImport) => {
    const line = $createHorizontalRuleNode();

    // TODO: Get rid of isImport flag
    if (isImport || parentNode.getNextSibling() != null) {
      parentNode.replace(line);
    } else {
      parentNode.insertBefore(line);
    }

    line.selectNext();
  },
  type: "element",
};

export const IMAGE: TextMatchTransformer = {
  dependencies: [ImageNode],
  export: (node) => {
    if (!$isImageNode(node)) {
      return null;
    }

    return `![${node.getAltText()}](${node.getSrc()})`;
  },
  importRegExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))/,
  regExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))$/,
  replace: (textNode, match) => {
    const [, altText, src] = match;
    const imageNode = $createImageNode({
      altText,
      maxWidth: 800,
      src,
    });
    textNode.replace(imageNode);
  },
  trigger: ")",
  type: "text-match",
};

export const EMOJI: TextMatchTransformer = {
  dependencies: [],
  export: () => null,
  importRegExp: /:([a-z0-9_]+):/,
  regExp: /:([a-z0-9_]+):$/,
  replace: (textNode, [, name]) => {
    const emoji = emojiList.find((e) => e.aliases.includes(name))?.emoji;
    if (emoji) {
      textNode.replace($createTextNode(emoji));
    }
  },
  trigger: ":",
  type: "text-match",
};

// Very primitive table setup
const TABLE_ROW_REG_EXP = /^(?:\|)(.+)(?:\|)\s?$/;
const TABLE_ROW_DIVIDER_REG_EXP = /^(\| ?:?-*:? ?)+\|\s?$/;

export const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],

  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) return null;

    return node
      .getChildren()
      .filter($isTableRowNode)
      .map((row) => {
        const cells = row
          .getChildren()
          .filter($isTableCellNode)
          .map((cell) => ({
            content: $convertToMarkdownString(TRANSFORMERS, cell)
              .replace(/\n/g, "\\n")
              .trim(),
            isHeader: cell.__headerState === TableCellHeaderStates.ROW,
          }));

        return generateRowMarkdown(cells);
      })
      .flat()
      .join("\n");
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _1, match) => {
    // Header row
    if (TABLE_ROW_DIVIDER_REG_EXP.test(match[0])) {
      const table = parentNode.getPreviousSibling();
      if (!table || !$isTableNode(table)) return;

      const lastRow = table.getChildren().slice(-1)[0];
      if (!lastRow || !$isTableRowNode(lastRow)) return;

      lastRow
        .getChildren()
        .filter($isTableCellNode)
        .forEach((cell) =>
          cell.setHeaderStyles(
            TableCellHeaderStates.ROW,
            TableCellHeaderStates.ROW,
          ),
        );

      parentNode.remove();
      return;
    }

    const matchCells = mapToTableCells(match[0]);
    if (matchCells == null) return;

    const siblingChain = Array.from(
      (function* () {
        for (
          let s = parentNode.getPreviousSibling();
          s;
          s = s.getPreviousSibling()
        ) {
          yield s;
        }
      })(),
    );

    const { rows, maxCells } = siblingChain.reduce(
      (acc, sibling) => {
        if (acc.break) return acc;

        if (!$isParagraphNode(sibling) || sibling.getChildrenSize() !== 1) {
          return { ...acc, break: true };
        }

        const firstChild = sibling.getFirstChild();
        if (!firstChild || !$isTextNode(firstChild)) {
          return { ...acc, break: true };
        }

        const text = firstChild.getTextContent();
        const cells = mapToTableCells(text);
        if (cells == null) return { ...acc, break: true };

        sibling.remove();
        return {
          rows: [cells, ...acc.rows],
          maxCells: Math.max(acc.maxCells, cells.length),
          break: false,
        };
      },
      { rows: [matchCells], maxCells: matchCells.length, break: false },
    );

    const table = $createTableNode();
    rows.forEach((cells) =>
      table.append(
        (() => {
          const tableRow = $createTableRowNode();
          Array.from({ length: maxCells }).forEach((_, i) =>
            tableRow.append(i < cells.length ? cells[i] : $createTableCell("")),
          );
          return tableRow;
        })(),
      ),
    );

    const previousSibling = parentNode.getPreviousSibling();
    if (
      $isTableNode(previousSibling) &&
      getTableColumnsSize(previousSibling) === maxCells
    ) {
      previousSibling.append(...table.getChildren());
      parentNode.remove();
    } else {
      parentNode.replace(table);
    }

    table.selectEnd();
  },

  type: "element",
};

function getTableColumnsSize(table: TableNode) {
  const row = table.getFirstChild();
  return $isTableRowNode(row) ? row.getChildrenSize() : 0;
}

const $createTableCell = (textContent: string): TableCellNode => {
  textContent = textContent.replace(/\\n/g, "\n");
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
  $convertFromMarkdownString(textContent, TRANSFORMERS, cell);
  return cell;
};

const mapToTableCells = (textContent: string): Array<TableCellNode> | null => {
  const match = textContent.match(TABLE_ROW_REG_EXP);
  if (!match || !match[1]) {
    return null;
  }
  return match[1].split("|").map((text) => $createTableCell(text));
};

function generateRowMarkdown(
  cells: { content: string; isHeader: boolean }[],
): string[] {
  const rowMarkdown = `| ${cells.map((cell) => cell.content).join(" | ")} |`;
  const headerMarkdown = cells.some((cell) => cell.isHeader)
    ? `| ${cells.map(() => "---").join(" | ")} |`
    : null;
  return headerMarkdown ? [rowMarkdown, headerMarkdown] : [rowMarkdown];
}

export const INPUT_TRANSFORMERS: Array<Transformer> = [
  TABLE,
  HR,
  IMAGE,
  EMOJI,
  CHECK_LIST,
  ...ELEMENT_TRANSFORMERS,
  ...MULTILINE_ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
];

export const TRANSFORMERS: Array<Transformer> = [
  EMOJI,
  ...ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
];
